import { Box, Button, Collapse, IconButton, makeStyles, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core'
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons'
import React, { useMemo, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { gregorianToJalali, thousandsSeparate, toFarsiDigits } from '../../assets/utils'
import { useReportFilter, ReportToolbar } from '../../assets/reportFilters'
import { exportExcelMulti, reportMetaNotes, fetchReportQr } from '../../assets/excelUtils'
import { guaranteesState } from '../../pages/contractor/atoms'
import ResponsiveTable from '../contracts/ResponsiveTable'
import { LoadMoreButton, useIncrementalList } from '../../hooks/useIncrementalList'

/** جدولِ تمدیدهای اعلامی به بانک (زیرِ ردیفِ ضمانت‌نامه) */
const DetailTable = ({ detail }) => {
    const rows = [...detail].sort((a, b) => new Date(b.sent_letter_date) - new Date(a.sent_letter_date))
    return (
        <Table size='small'>
            <TableHead>
                <TableRow>
                    <TableCell align='center'><strong>ردیف</strong></TableCell>
                    <TableCell align='center'><strong>شماره نامه ارسالی به بانک</strong></TableCell>
                    <TableCell align='center'><strong>تاریخ</strong></TableCell>
                    <TableCell align='center'><strong>شماره پاسخ بانک</strong></TableCell>
                    <TableCell align='center'><strong>تاریخ</strong></TableCell>
                    <TableCell align='center'><strong>مبلغ اعلامی به بانک</strong></TableCell>
                    <TableCell align='center'><strong>تاریخ انقضا اعلامی به بانک</strong></TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row, index) => (
                    <TableRow key={row.id}>
                        <TableCell align='center'><strong>{toFarsiDigits(index + 1)}</strong></TableCell>
                        <TableCell align='center'>{toFarsiDigits(row.sent_letter_number)}</TableCell>
                        <TableCell align='center'>{gregorianToJalali(row.sent_letter_date)}</TableCell>
                        <TableCell align='center'>{toFarsiDigits(row.received_letter_number)}</TableCell>
                        <TableCell align='center'>{gregorianToJalali(row.received_letter_date)}</TableCell>
                        <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.new_guarantee_amount))}</TableCell>
                        <TableCell align='center'>{gregorianToJalali(row.new_guarantee_expire_date)}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    )
}

/** خروجی اکسل ضمانت‌نامهها — برگهای: ضمانت‌نامهها + تمدیدهای اعلامی به بانک */
const exportGuaranteesExcel = async (contract, guarantees) => {
    const qr = contract && contract.id ? await fetchReportQr(contract.id) : null

    const main = {
        caption: 'ضمانت‌نامه‌ها',
        headers: ['ردیف', 'نوع', 'بانک', 'موضوع', 'شماره', 'مبلغ اولیه', 'تاریخ صدور', 'تاریخ انقضاء', 'مبلغ فعلی', 'تاریخ انقضاء فعلی', 'شماره ابطال', 'تاریخ ابطال', 'شماره ضبط', 'تاریخ ضبط'],
        rows: guarantees.map((g, i) => [
            i + 1,
            g.type,
            g.bank,
            g.subject,
            g.number,
            g.amount,
            gregorianToJalali(g.issue_date),
            gregorianToJalali(g.expire_date),
            g.final_amount,
            gregorianToJalali(g.latest_expire_date),
            g.repeal_number,
            gregorianToJalali(g.repeal_date),
            g.confiscation_number,
            gregorianToJalali(g.confiscation_date),
        ]),
    }

    const extensions = {
        caption: 'تمدیدهای اعلامی به بانک',
        headers: ['شماره ضمانت‌نامه', 'بانک', 'شماره نامه ارسالی', 'تاریخ نامه', 'شماره پاسخ بانک', 'تاریخ پاسخ', 'مبلغ اعلامی', 'تاریخ انقضای اعلامی'],
        rows: [],
    }
    guarantees.forEach((g) => {
        const detail = [...(g.detail || [])].sort((a, b) => new Date(b.sent_letter_date) - new Date(a.sent_letter_date))
        detail.forEach((d) => {
            extensions.rows.push([g.number, g.bank, d.sent_letter_number, gregorianToJalali(d.sent_letter_date), d.received_letter_number, gregorianToJalali(d.received_letter_date), d.new_guarantee_amount, gregorianToJalali(d.new_guarantee_expire_date)])
        })
    })

    const title = `ضمانت‌نامه‌ها — ${contract && contract.contractee ? contract.contractee : ''}${contract && contract.number ? ' | قرارداد ' + contract.number : ''}`
    exportExcelMulti({
        filename: 'guarantees.xlsx',
        title,
        headers: main.headers,
        notes: reportMetaNotes(),
        qrDataUri: qr,
        sections: [
            {
                ...main,
                totalRow: (() => {
                    const tr = main.headers.map(() => '')
                    tr[0] = 'جمع'
                    tr[5] = guarantees.reduce((a, g) => a + (Number(g.amount) || 0), 0)      // مبلغ اولیه
                    tr[8] = guarantees.reduce((a, g) => a + (Number(g.final_amount) || 0), 0) // مبلغ فعلی
                    return tr
                })(),
            },
            extensions,
        ],
    })
}

const Guarantees = props => {
    const guarantees = useRecoilValue(guaranteesState)
    const contract = props.contract
    const [expandedId, setExpandedId] = useState(null)

    const extract = useMemo(() => ({
        search: (g) => [g.number, g.bank, g.subject, g.type, g.amount, g.final_amount, g.repeal_number, g.confiscation_number],
    }), [])
    const { search, setSearch, year, setYear, years, filtered } = useReportFilter(guarantees, extract)

    // رندرِ تدریجی (گزارش‌های بلند روی موبایل فریز می‌کردند)
    const { visible, hasMore, loadMore, remaining } = useIncrementalList(filtered, {
        step: 50,
        resetKey: `${search}|${year}`,
    })

    const columns = useMemo(() => [
        {
            key: 'expand', label: '', width: 44,
            render: (g) => (
                <IconButton
                    disabled={!g.detail || g.detail.length === 0}
                    size='small'
                    onClick={() => setExpandedId(expandedId === g.id ? null : g.id)}
                >
                    {expandedId === g.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </IconButton>
            ),
        },
        { key: 'type', label: 'نوع', primary: true, render: (g) => g.type },
        { key: 'bank', label: 'بانک', render: (g) => toFarsiDigits(g.bank) },
        { key: 'subject', label: 'موضوع', render: (g) => toFarsiDigits(g.subject) },
        { key: 'number', label: 'شماره', render: (g) => toFarsiDigits(g.number) },
        { key: 'amount', label: 'مبلغ', render: (g) => toFarsiDigits(thousandsSeparate(g.amount)) },
        { key: 'issue_date', label: 'تاریخ صدور', render: (g) => gregorianToJalali(g.issue_date) },
        { key: 'expire_date', label: 'تاریخ انقضاء', render: (g) => gregorianToJalali(g.expire_date) },
        { key: 'final_amount', label: 'مبلغ فعلی', render: (g) => toFarsiDigits(thousandsSeparate(g.final_amount)) },
        { key: 'latest_expire_date', label: 'تاریخ انقضاء فعلی', render: (g) => gregorianToJalali(g.latest_expire_date) },
        { key: 'repeal_number', label: 'شماره ابطال', render: (g) => toFarsiDigits(g.repeal_number) },
        { key: 'repeal_date', label: 'تاریخ ابطال', render: (g) => gregorianToJalali(g.repeal_date) },
        { key: 'confiscation_number', label: 'شماره ضبط', render: (g) => toFarsiDigits(g.confiscation_number) },
        { key: 'confiscation_date', label: 'تاریخ ضبط', render: (g) => gregorianToJalali(g.confiscation_date) },
    ], [expandedId])

    return (
        <Box>
            <ReportToolbar search={search} setSearch={setSearch} year={year} setYear={setYear} years={years} />
            {guarantees.length > 0 && (
                <Box style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
                    <Button variant='contained' color='secondary' onClick={() => exportGuaranteesExcel(contract, guarantees)}>
                        دانلود اکسل
                    </Button>
                </Box>
            )}
            <Box>
                <ResponsiveTable
                    columns={columns}
                    rows={visible}
                    showIndex
                    indexLabel="ردیف"
                    maxHeight={450}
                    emptyText="ضمانت‌نامه‌ای یافت نشد."
                    expandedKey={expandedId}
                    renderExpanded={(g) => (g.detail && g.detail.length ? <DetailTable detail={g.detail} /> : null)}
                />
                <LoadMoreButton hasMore={hasMore} remaining={remaining} onClick={loadMore} />
            </Box>
        </Box>
    )
}
export default Guarantees
